import { useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Home, Briefcase, ClipboardCheck, CircleDollarSign, Moon, Sun, LogOut, Loader2, ArrowLeftRight } from "lucide-react";
import { HomeDashboard } from "./features/home/HomeDashboard";
import { CaseListPage } from "./features/bidding/CaseListPage";
import { CaseDetailPage } from "./features/bidding/CaseDetailPage";
import { MyBidsPage } from "./features/bidding/MyBidsPage";
import { EarningsPage } from "./features/earnings/EarningsPage";
import { NotificationBell } from "./features/notifications/NotificationBell";
import { useSession } from "./features/auth/useSession";
import { ensureAnonSession } from "./lib/anonAuth";
import { useThemeStore } from "./store/theme";
import { supabase } from "./lib/supabase";
import { api } from "./lib/api";

type Tab = "home" | "cases" | "mybids" | "earnings";

const TAB_TITLES: Record<Tab, string> = {
  home: "シタミー業者版",
  cases: "案件一覧",
  mybids: "受注管理",
  earnings: "売上",
};

/** 画面状態の保持。URL(パス)を正とし、localStorageは補助（ルート表示時の復元）。 */
const V_TAB_KEY = "sitame_v_tab";
const V_DETAIL_KEY = "sitame_v_detail";

/** タブ＋案件ID → URLパス。各画面を https://pro.sita-me.com/xx に分ける。 */
function vPathFor(tab: Tab, detailId: string | null): string {
  if (detailId) return `/cases/${detailId}`;
  if (tab === "cases") return "/cases";
  if (tab === "mybids") return "/mybids";
  if (tab === "earnings") return "/earnings";
  return "/"; // home
}
/** 現在のURLパス → 画面状態。案件詳細は基底タブを cases として扱う。 */
function vParsePath(): { tab: Tab; detailId: string | null } {
  const p = window.location.pathname;
  if (p.startsWith("/cases/")) return { tab: "cases", detailId: decodeURIComponent(p.slice("/cases/".length)) || null };
  if (p.startsWith("/cases")) return { tab: "cases", detailId: null };
  if (p.startsWith("/mybids")) return { tab: "mybids", detailId: null };
  if (p.startsWith("/earnings")) return { tab: "earnings", detailId: null };
  return { tab: "home", detailId: null };
}
/** 初期状態：URLを優先。ルート("/")のときのみ localStorage から前回画面を復元。 */
function vInitialState(): { tab: Tab; detailId: string | null } {
  if (window.location.pathname !== "/") return vParsePath();
  try {
    const t = localStorage.getItem(V_TAB_KEY);
    const d = localStorage.getItem(V_DETAIL_KEY) || null;
    if (d) return { tab: "cases", detailId: d };
    if (t === "cases" || t === "mybids" || t === "earnings") return { tab: t, detailId: null };
  } catch { /* noop */ }
  return { tab: "home", detailId: null };
}

interface AppProps {
  /**
   * 顧客アプリ（iOS）に組み込まれて動くときだけ渡される（2026-10-31 1アプリで顧客側・業者側を分ける）。
   * 指定時はヘッダに「お客様モードへ切替」ボタンを出す。Web版（pro.sita-me.com）では未指定＝従来どおり。
   */
  onSwitchToCustomer?: () => void;
}

export function App({ onSwitchToCustomer }: AppProps = {}) {
  const { session, loading } = useSession();
  const initRef = useRef(vInitialState());
  const [tab, setTab] = useState<Tab>(initRef.current.tab);
  const [detailId, setDetailId] = useState<string | null>(initRef.current.detailId);
  const mode = useThemeStore((s) => s.mode);
  const setMode = useThemeStore((s) => s.setMode);

  // 受注管理タブの「要対応」バッジ（工事日未確定/完了未報告の受注件数）。
  const { data: actionable = 0 } = useQuery({
    queryKey: ["actionable-orders"],
    queryFn: () => api.countActionableOrders(),
    enabled: !!session,
    refetchInterval: 30_000,
  });

  // 画面状態をURL(パス)へ同期：戻る/更新/共有に対応。localStorageは補助。
  useEffect(() => {
    try {
      localStorage.setItem(V_TAB_KEY, tab);
      if (detailId) localStorage.setItem(V_DETAIL_KEY, detailId); else localStorage.removeItem(V_DETAIL_KEY);
    } catch { /* noop */ }
    const target = vPathFor(tab, detailId);
    if (window.location.pathname !== target) window.history.pushState({}, "", target);
  }, [tab, detailId]);

  // ブラウザの戻る/進む → URLから画面を復元
  useEffect(() => {
    const onPop = () => { const s = vParsePath(); setTab(s.tab); setDetailId(s.detailId); };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  // アカウント概念のUIUX排除：セッションが無ければ匿名ユーザーを裏で自動発行（2026-06-12 社長要望）
  useEffect(() => {
    if (!loading && !session) void ensureAnonSession();
  }, [loading, session]);

  // 業者アカウントは open 案件閲覧に role=vendor が必要。初回ログイン時に昇格（customerのみ）。
  // role=vendor になると vendor_details が自動作成・自動承認される（sql/09）。
  useEffect(() => {
    const uid = session?.user.id;
    if (!uid) return;
    supabase.from("profiles").update({ role: "vendor" }).eq("id", uid).eq("role", "customer").then(() => {});
  }, [session]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--color-bg)] text-[var(--color-fg)]">
        <Loader2 className="animate-spin" />
      </div>
    );
  }
  // 匿名セッション確立中（初回アクセスの一瞬のみ）
  if (!session) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--color-bg)] text-[var(--color-fg)]">
        <Loader2 className="animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col max-w-md mx-auto bg-gray-50 dark:bg-[var(--color-bg)] text-[var(--color-fg)]">
      {/* 旧デモUI: 白スティッキーヘッダー＋中央タイトル */}
      <header className="safe-pt sticky top-0 z-10 bg-white dark:bg-white/5 border-b border-[var(--color-border)]">
        <div className="px-4 py-3 flex items-center">
          <div className="flex-1 flex items-center min-w-0">
            <img src="/brand/sirasu-mark.png?v=3" alt="SitaMe" className="h-7 w-auto flex-shrink-0" />
          </div>
          <h1 className="text-sm font-bold flex-shrink-0 text-center px-2">
            {detailId ? "案件詳細" : TAB_TITLES[tab]}
          </h1>
          <div className="flex-1 flex justify-end items-center gap-0.5">
            <NotificationBell onOpenCase={() => { setDetailId(null); setTab("mybids"); }} />
            <button aria-label="テーマ切替" onClick={() => setMode(mode === "dark" ? "light" : "dark")} className="p-2 rounded hover:bg-brand-50 dark:hover:bg-brand-800/30">
              {mode === "dark" ? <Sun size={16} /> : <Moon size={16} />}
            </button>
            {!(session.user as { is_anonymous?: boolean }).is_anonymous && (
              <button aria-label="ログアウト" onClick={() => supabase.auth.signOut()} className="p-2 rounded hover:bg-brand-50 dark:hover:bg-brand-800/30">
                <LogOut size={16} />
              </button>
            )}
          </div>
        </div>
        {onSwitchToCustomer && (
          <div className="px-4 py-1.5 flex items-center justify-between border-t border-[var(--color-border)] bg-brand-50 dark:bg-brand-800/20">
            <span className="text-[11px] font-bold text-brand-700 dark:text-brand-200">業者モードで利用中</span>
            <button aria-label="お客様モードへ切替" onClick={onSwitchToCustomer} className="flex items-center gap-1 text-[11px] font-bold text-brand-600 dark:text-brand-300 whitespace-nowrap">
              <ArrowLeftRight size={13} /> お客様モードに切替
            </button>
          </div>
        )}
      </header>

      <main className="flex-1 overflow-y-auto pb-16">
        {detailId ? (
          <CaseDetailPage caseId={detailId} onBack={() => setDetailId(null)} />
        ) : tab === "home" ? (
          <HomeDashboard onGoJobs={() => setTab("cases")} onGoManage={() => setTab("mybids")} />
        ) : tab === "cases" ? (
          <CaseListPage onOpen={setDetailId} />
        ) : tab === "mybids" ? (
          <MyBidsPage onOpenCase={setDetailId} />
        ) : (
          <EarningsPage />
        )}
      </main>

      {/* 旧デモUI: 4タブ下部ナビ */}
      <nav className="safe-pb fixed bottom-0 inset-x-0 bg-white dark:bg-[var(--color-bg)] border-t border-[var(--color-border)] z-20">
        <div className="max-w-md mx-auto grid grid-cols-4">
          <TabBtn active={tab === "home" && !detailId} onClick={() => { setDetailId(null); setTab("home"); }} icon={<Home size={20} />} label="ホーム" />
          <TabBtn active={tab === "cases" && !detailId} onClick={() => { setDetailId(null); setTab("cases"); }} icon={<Briefcase size={20} />} label="案件一覧" />
          <TabBtn active={tab === "mybids" && !detailId} onClick={() => { setDetailId(null); setTab("mybids"); }} icon={<ClipboardCheck size={20} />} label="受注管理" badge={actionable} />
          <TabBtn active={tab === "earnings" && !detailId} onClick={() => { setDetailId(null); setTab("earnings"); }} icon={<CircleDollarSign size={20} />} label="売上" />
        </div>
      </nav>
    </div>
  );
}

function TabBtn({ active, onClick, icon, label, badge = 0 }: { active: boolean; onClick: () => void; icon: React.ReactNode; label: string; badge?: number }) {
  return (
    <button onClick={onClick} className={`relative flex flex-col items-center gap-0.5 py-2 ${active ? "text-brand-600 dark:text-brand-300" : "text-gray-400 dark:text-white/50"}`}>
      <span className="relative">
        {icon}
        {badge > 0 && (
          <span className="absolute -top-1.5 -right-2 min-w-[16px] h-[16px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center leading-none">
            {badge > 9 ? "9+" : badge}
          </span>
        )}
      </span>
      <span className="text-[10px] font-bold">{label}</span>
    </button>
  );
}
